import React from 'react';
import { useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { Popconfirm, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { editIntegrationAtom, partnerAtom } from '../atoms';
import { ActionWrapper, StyledButton, TableContainer } from './styled';
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Retry } from '@src/components/common';
import {
  isAdminRoleSelector,
  siPagerDutyDisplayFeatureSelector,
} from '@src/store/common/selectors';
import { useUpdateIntegrationStatus, useFetchServiceIntegrationsList } from './hooks';
import moment from 'moment';
import { SERVICE_INT_DATE_FORMAT } from '@src/utils/constants';
import { ServiceIntegration } from '@src/components/notification-channel/ServiceIntegrations/types';
import Summary from './Summary';
import AvailableIntegrations from './AvailableIntegrations';

const ServiceIntegrationsList = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const setEditIntegration = useSetAtom(editIntegrationAtom);
  const setPartner = useSetAtom(partnerAtom);
  const isAdminUser = useSelector(isAdminRoleSelector);
  const { mutate: updateIntegrationStatus } = useUpdateIntegrationStatus();
  const displaySiPagerduty = useSelector(siPagerDutyDisplayFeatureSelector);

  const {
    data: serviceIntegrationsData = [],
    isFetching,
    isError,
    refetch,
  } = useFetchServiceIntegrationsList(isDrawerOpen);

  const handleEdit = (rowData: ServiceIntegration) => {
    setPartner({ partnerId: rowData.partnerId || undefined, name: rowData.partnerName });

    // this causes edit form to open
    setEditIntegration({ configId: rowData.configId || null });
  };

  const handleDelete = async (configId: number) => {
    await updateIntegrationStatus({ configId: configId, state: 0 });
  };

  const handleDisable = async (configId: number) => {
    await updateIntegrationStatus({ configId: configId, state: 1 });
  };

  const handleEnable = async (configId: number) => {
    await updateIntegrationStatus({ configId: configId, state: 2 });
  };

  const columns: ColumnsType<ServiceIntegration> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
    },
    {
      title: 'Channel Type',
      dataIndex: 'partnerName',
      key: 'partnerName',
      width: '20%',
      sorter: (a, b) => String(a.partnerName).localeCompare(String(b.partnerName)),
    },
    {
      title: 'Updated on',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '20%',
      render(_k, row: any) {
        return moment(row.updatedAt).format(SERVICE_INT_DATE_FORMAT);
      },
      sorter: (a, b) => (moment(a.updatedAt).isBefore(moment(b.updatedAt)) ? -1 : 1),
    },
    {
      title: 'Alert Type',
      dataIndex: 'alertType',
      key: 'alertType',
      width: '15%',
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      align: 'center',
      render: (status: number) => {
        switch (status) {
          case 3:
            return (
              <Tooltip
                title={'This integration has an error. Please check the details.'}
                placement="top"
              >
                <div style={{ color: 'red' }}>Error</div>
              </Tooltip>
            );
          case 1:
            return <div style={{ color: 'grey' }}>Disabled</div>;
          case 2:
            return <div style={{ color: 'green' }}>Enabled</div>;
          default:
            return <div style={{ color: 'grey' }}>Disabled</div>;
        }
      },
    },
    {
      key: 'action',
      align: 'center',
      width: '15%',
      render(_k, row) {
        return (
          <ActionWrapper>
            {isAdminUser && row.state !== 1 ? (
              <Tooltip
                title={
                  row.state === 3 ? 'Integration error! Please edit and verify details' : 'Edit'
                }
              >
                <StyledButton
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => row.configId && handleEdit(row)}
                />
              </Tooltip>
            ) : (
              <Tooltip title="View">
                <StyledButton
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => row.configId && handleEdit(row)}
                />
              </Tooltip>
            )}
            {row.state !== 2 ? (
              <Tooltip title="Enable">
                <Popconfirm
                  key="enable"
                  placement="left"
                  title="Enable Integration"
                  description="Do you want to enable this integration?"
                  icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                  okText="Enable"
                  cancelText="Cancel"
                  onConfirm={() => row.configId && handleEnable(row.configId)}
                  disabled={!isAdminUser || row.state === 3}
                >
                  <StyledButton
                    disabled={!isAdminUser || row.state === 3}
                    type="link"
                    icon={<BellOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            ) : (
              <Tooltip title="Disable">
                <Popconfirm
                  key="disable"
                  placement="left"
                  title="Disable Integration"
                  description="Do you want to disable this integration?"
                  icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                  okText="Disable"
                  cancelText="Cancel"
                  onConfirm={() => row.configId && handleDisable(row.configId)}
                  disabled={!isAdminUser}
                >
                  <StyledButton disabled={!isAdminUser} type="link" icon={<StopOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <Popconfirm
                key="delete"
                placement="left"
                title="Delete Integration"
                description="Do you want to delete this integration?"
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                okText="Delete"
                cancelText="Cancel"
                onConfirm={() => row.configId && handleDelete(row.configId)}
                disabled={!isAdminUser}
              >
                <StyledButton disabled={!isAdminUser} type="link" icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </ActionWrapper>
        );
      },
    },
  ];

  // Fail-Safe: In case there is issue with AAA-849 PagerDuty migration,
  // code need not be reverted if FF is disabled
  const filteredServiceIntegrationData = displaySiPagerduty
    ? serviceIntegrationsData
    : serviceIntegrationsData.filter((row) => row.partnerName !== 'PagerDuty');

  return (
    <TableContainer>
      <Summary data={filteredServiceIntegrationData} />
      <Retry isLoading={isFetching} fetchData={refetch} showError={isError}>
        <Table
          sticky
          dataSource={filteredServiceIntegrationData}
          columns={columns}
          rowKey={(es: any) => es.confidId || null}
          showSorterTooltip={false}
          scroll={{ x: true }}
          pagination={false}
          rowClassName={(row) => (row.state !== 2 ? 'row-disabled' : '')}
        />
      </Retry>
      <AvailableIntegrations isDrawerOpen={isDrawerOpen} />
    </TableContainer>
  );
};

export default ServiceIntegrationsList;
